import React, { useState } from "react";
import { GetAdminCoursesSlim } from "../../actions/adminQueries";
import { useQueryClient } from "@tanstack/react-query";
import { Table, Input, Button } from "reactstrap";
import CreateCourse from "../../components/Modal/CreateCourse";
import { PacmanLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CourseAdminNav from "../../components/Navigation/CourseAdminNav";
import axios from "../../services/api/api";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import { API_BASE_URL } from "../../constants/api.constants";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { useIntl } from "react-intl";
import { PiCheckCircle, PiWarningCircle } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import FilterSelect from "../../components/Inputs/FilterSelect";

const Filters = {
  ALL: "all",
  UNSORTED_MODULES: "unsortedModules",
  WITHOUT_MODULES: "withoutModules",
  MISSING_TEACHER_GUIDE: "missingTeacherGuide",
  MISSING_GLOSSARY: "missingGlossary",
};

const hasUnsortedModules = (course) => {
  const courseModules = course?.courseModules ?? [];
  return (
    courseModules.length >= 2 && courseModules.some((cm) => cm.sortOrder === 0)
  );
};

function CoursesList() {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const courses = GetAdminCoursesSlim();

  const [filterSearch, setFilterSearch] = useState("");
  const [attentionFilter, setAttentionFilter] = useState(Filters.ALL);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [updatingCourse, setUpdatingCourse] = useState([]);

  const updateCourses = () => {
    courses.refetch();
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const selectCourse = (course, programLicenses) => {
    course.programLicenses = programLicenses;
    setSelectedCourse(course);
    toggleModal();
  };

  const deleteCourse = (courseId) => {
    axios
      .delete("/Course/" + courseId)
      .then((response) => {})
      .catch((err) => {
        toast.warn(err.response.data);
      });

    queryClient.setQueryData(["FetchAdminCoursesSlim"], (old) =>
      (old || []).filter((course) => course.id !== courseId)
    );
  };

  const setHideStatus = (courseId, hideForStudents, hideForTeachers) => {
    axios
      .post(`${API_BASE_URL}/Course/SetHideStatus/`, {
        id: courseId,
        hideForStudents: hideForStudents,
        hideForTeachers: hideForTeachers,
      })
      .then(() => {
        toast.success(intl.formatMessage({ id: "HideStatusChanged" }), TOAST_CONFIG);
        updateCourses();
      })
      .catch((err) => {
        toast.warn(
          err && err.response && err.response.data
            ? err.response.data
            : intl.formatMessage({ id: "CouldNotSetHideStatus" }),
          TOAST_CONFIG
        );
      });
  };

  const createNew = () => {
    setSelectedCourse(null);
    toggleModal();
  };

  const handleFilterSearch = (event) => {
    setFilterSearch(event.target.value);
  };

  const handleToggleCourseAsLoading = (courseId) => {
    if (updatingCourse.includes(courseId)) {
      setUpdatingCourse(updatingCourse.filter((course) => course !== courseId));
      updateCourses();
    } else {
      setUpdatingCourse([...updatingCourse, courseId]);
    }
  };

  if (courses.isPending === true) {
    return (
      <div
        className="sweet-loading"
        style={{ position: "fixed", top: "50%", left: "50%" }}
      >
        <PacmanLoader color={"#20a8d8"} loading={true} />
      </div>
    );
  }

  let coursesFilteredByName = null;
  let coursesFilteredByDescription = null;

  var filteredCourses = courses.data || [];
  const searchTerm = filterSearch.trim().toLowerCase();
  if (searchTerm.length > 0) {
    coursesFilteredByName = filteredCourses.filter((it) =>
      (it.course.name ?? "").toLowerCase().includes(searchTerm),
    );
    coursesFilteredByDescription = filteredCourses.filter((it) =>
      (it.course.description ?? "").toLowerCase().includes(searchTerm),
    );
    let tempAllFilteredCourses = [...coursesFilteredByName];
    coursesFilteredByDescription.forEach((fd) => {
      if (
        !tempAllFilteredCourses.some((to) => to.course.id === fd.course.id)
      ) {
        tempAllFilteredCourses.push(fd);
      }
    });
    filteredCourses = tempAllFilteredCourses;
  }

  if (attentionFilter !== Filters.ALL) {
    filteredCourses = filteredCourses.filter((item) => {
      switch (attentionFilter) {
        case Filters.UNSORTED_MODULES:
          return hasUnsortedModules(item.course);
        case Filters.WITHOUT_MODULES:
          return (item.course?.courseModules ?? []).length === 0;
        case Filters.MISSING_TEACHER_GUIDE:
          return item.course.teacherGuideFileUrl == null;
        case Filters.MISSING_GLOSSARY:
          return item.course.glossaryFileUrl == null;
        default:
          return true;
      }
    });
  }

  let availableProgramLicenses = [];

  (courses.data ?? []).forEach((item) => {
    if (item.programs?.length > 0) {
      item.programs.forEach((prog) => {
        if (
          availableProgramLicenses.filter(
            (x) => x.licenseName === prog.licenseName
          ).length === 0
        ) {
          availableProgramLicenses.push(prog);
        }
      });
    }
  });

  const attentionOptions = [
    { value: Filters.ALL, label: intl.formatMessage({ id: "All" }) },
    {
      value: Filters.UNSORTED_MODULES,
      label: intl.formatMessage({ id: "UnsortedModules" }),
    },
    {
      value: Filters.WITHOUT_MODULES,
      label: intl.formatMessage({ id: "WithoutModules" }),
    },
    {
      value: Filters.MISSING_TEACHER_GUIDE,
      label: intl.formatMessage({ id: "MissingTeacherGuide" }),
    },
    {
      value: Filters.MISSING_GLOSSARY,
      label: intl.formatMessage({ id: "MissingGlossary" }),
    },
  ];

  const filtersAreActive =
    filterSearch.trim() !== "" || attentionFilter !== Filters.ALL;

  const clearFilters = () => {
    setFilterSearch("");
    setAttentionFilter(Filters.ALL);
  };

  var mappedCourses = filteredCourses.map((course, id) => {
    var programs = [];
    if (course.programs?.length > 0) {
      course.programs.forEach((license) => {
        if (programs.indexOf(license.licenseName) === -1)
          programs.push(license.licenseName);
      });
    }
    return (
      <tr key={id}>
        <td>
          <div
            className={
              course.course.imgUrl ? "avatar float-start" : "d-none"
            }
            style={{ marginRight: "10px" }}
          >
            {course.course.imgUrl ? (
              <img src={course.course.imgUrl} className="img-avatar" alt="" />
            ) : (
              ""
            )}
          </div>
        </td>

        <td>
          <Button
            size="sm"
            color="link"
            title={intl.formatMessage({ id: "EditCourse" }, { courseName: course.course.name })}
            onClick={() => selectCourse(course.course, programs)}
            className="text-start w-100"
          >
            {course.course.name}
          </Button>
          {updatingCourse.includes(course.course.id) ? (
            <p>
              <PacmanLoader color={"#20a8d8"} loading={true} size={10} />
            </p>
          ) : (
            ""
          )}
        </td>
        <td>
          {hasUnsortedModules(course.course) && (
            <PiWarningCircle
              className="warning-icon"
              title={intl.formatMessage({
                id: "ModulesNotSorted",
              })}
            />
          )}
        </td>
        <td>
          {course.course.teacherGuideFileUrl != null && (
            <PiCheckCircle className="text-success fs-5" />
          )}
        </td>
        <td>
          {course.course.glossaryFileUrl != null && (
            <PiCheckCircle className="text-success fs-5" />
          )}
        </td>
        <td className="text-end">{course.course.id}</td>
        <td>{course.course.code}</td>
        <td>{course.course.description}</td>
        <td className="text-end">
          <Button
            size="sm"
            outline
            color="primary"
            title={intl.formatMessage(
              { id: "EditCourseModules" },
              { courseName: course.course.name }
            )}
            onClick={() =>
              navigate("/superuser/courses/coursemodules", {
                state: {
                  courseId: course.course.id,
                  courseName: course.course.name,
                },
              })
            }
          >
            {course.course.courseModules.length}
          </Button>
        </td>
        <td title={programs.join(", ")} className="text-end">
          <span>{programs.length}</span>
        </td>
        <td align="center">
          <Button
            size="sm"
            outline
            color="blue"
            onClick={() =>
              setHideStatus(
                course.course.id,
                !course.course.hideCourseForStudents,
                course.course.hideCourseForTeachers
              )
            }
          >
            <FontAwesomeIcon
              icon="check"
              opacity={course.course.hideCourseForStudents ? 1 : 0}
            />
          </Button>
        </td>
        <td align="center">
          <Button
            size="sm"
            outline
            color="blue"
            onClick={() =>
              setHideStatus(
                course.course.id,
                course.course.hideCourseForStudents,
                !course.course.hideCourseForTeachers
              )
            }
          >
            <FontAwesomeIcon
              icon="check"
              opacity={course.course.hideCourseForTeachers ? 1 : 0}
            />
          </Button>
        </td>
        <td>
          <Button
            size="sm"
            outline
            color="danger"
            title={intl.formatMessage({ id: "DeleteCourseName" }, { courseName: course.course.name })}
            onClick={() => deleteCourse(course.course.id)}
          >
            <FontAwesomeIcon icon="trash" />
          </Button>
        </td>
      </tr>
    );
  });

  return (
    <div>
      <CourseAdminNav location="courses" />

      <div className="container-fluid admin-container">
        <div className="bg-light rounded p-3 mb-3 d-flex align-items-end flex-wrap gap-3">
          <Input
            bsSize="sm"
            className="search-input"
            style={{ width: "220px" }}
            placeholder={intl.formatMessage({
              id: "View.Student.SearchCourse",
            })}
            value={filterSearch}
            onChange={handleFilterSearch}
          />
          <FilterSelect
            label={intl.formatMessage({ id: "NeedsAttention" })}
            options={attentionOptions}
            value={attentionFilter}
            onChange={setAttentionFilter}
          />
          <span className="text-muted small">
            <FormattedMessage
              id="ShowingCoursesCount"
              values={{
                shown: filteredCourses.length,
                total: (courses.data ?? []).length,
              }}
            />
          </span>
          {filtersAreActive ? (
            <Button
              size="sm"
              color="link"
              className="p-0"
              onClick={clearFilters}
            >
              <FormattedMessage id="ResetAll" />
            </Button>
          ) : (
            ""
          )}
          <Button
            size="md"
            outline
            color="primary"
            className="ms-auto"
            onClick={() => {
              createNew();
            }}
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <span className="">
              <FormattedMessage id="AddCourse" />
            </span>
          </Button>
        </div>
        <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Table hover striped className="school-table">
            <thead className="sticky-header">
              <tr>
                <th />
                <th><FormattedMessage id="Name" /></th>
                <th></th>
                <th>
                  <FormattedMessage id="Guide" />
                </th>
                <th>
                  <FormattedMessage id="GlossaryShort" />
                </th>
                <th>
                  <FormattedMessage id="CourseId" />
                </th>
                <th><FormattedMessage id="Code" /></th>
                <th><FormattedMessage id="Description" /></th>
                <th><FormattedMessage id="Modules" /></th>
                <th><FormattedMessage id="ProgramLicenses" /></th>
                <th><FormattedMessage id="HiddenForStudents" /></th>
                <th><FormattedMessage id="HiddenForTeachers" /></th>
                <th><FormattedMessage id="Delete" /></th>
              </tr>
            </thead>
            <tbody>
              {mappedCourses.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center text-muted py-4">
                    <FormattedMessage id="NoMatches" />
                  </td>
                </tr>
              ) : (
                mappedCourses
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <CreateCourse
        isOpen={modalIsOpen}
        toggle={toggleModal}
        toggleExpand={toggleExpand}
        isExpanded={isExpanded}
        updateCourse={selectedCourse}
        refreshList={updateCourses}
        availableProgramLicenses={availableProgramLicenses}
        handleToggleCourseAsLoading={handleToggleCourseAsLoading}
      />
    </div>
  );
}

export default CoursesList;
